import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "@shadcn/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@shadcn/components/ui/table";
import { Button } from "@shadcn/components/ui/button";
import { Download, ChevronDown, ChevronRight } from 'lucide-react';
import { Pie } from 'react-chartjs-2';
import { format } from "date-fns";
import * as XLSX from 'xlsx';

const ByStoreTypeTab = ({
  data,
  hierarchicalData,
  pieData,
  loading,
  filters,
  groupBy
}) => {
  // State for collapsible sections - only store type level
  const [expandedStoreTypes, setExpandedStoreTypes] = useState({});

  const toggleStoreType = (storeType) => {
    setExpandedStoreTypes(prev => ({
      ...prev,
      [storeType]: !prev[storeType]
    }));
  };
  const exportToExcel = () => {
    if (!hierarchicalData?.hierarchicalData || !hierarchicalData?.warehouses) return;

    try {
      // Prepare the workbook
      const workbook = XLSX.utils.book_new();

      // Main data worksheet
      const mainData = [];

      // Always show all columns in export (store type, category, product, sku)
      const headers = ['Store Type', 'Category', 'Product', 'SKU', 'Quantity', 'Percentage'];
      mainData.push(headers);

      const { hierarchicalData: hData, warehouses, total } = hierarchicalData;

      // Create flat rows with all details
      warehouses.forEach(storeType => {
        Object.entries(hData[storeType] || {}).forEach(([category, categoryData]) => {
          Object.entries(categoryData.products || {}).forEach(([product, productData]) => {
            Object.entries(productData.skus || {}).forEach(([sku, skuData]) => {
              mainData.push([
                storeType,
                category,
                product,
                sku,
                skuData.quantity,
                total > 0 ? ((skuData.quantity / total) * 100).toFixed(1) + '%' : '0%'
              ]);
            });
          });
        });
      });

      // Add grand total row
      const totalRow = ['GRAND TOTAL'];
      for (let i = 1; i < headers.length - 2; i++) {
        totalRow.push('');
      }
      totalRow.push(total || 0, '100%');
      mainData.push(totalRow);

      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(mainData);

      // Set column widths for all columns
      const columnWidths = [
        { wch: 20 }, // Store Type
        { wch: 20 }, // Category
        { wch: 25 }, // Product
        { wch: 15 }, // SKU
        { wch: 12 }, // Quantity
        { wch: 12 }  // Percentage
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Store Type Data');

      // Summary worksheet
      const summaryData = [];

      // Summary headers
      summaryData.push(['Store Type Report Summary']);
      summaryData.push(['']);

      // Report period
      if (filters?.startDate && filters?.endDate) {
        summaryData.push(['Report Period:', `${format(filters.startDate, "dd/MM/yyyy")} to ${format(filters.endDate, "dd/MM/yyyy")}`]);
      }

      // Filter information
      summaryData.push(['']);
      summaryData.push(['Report Details:']);
      summaryData.push(['Group By:', groupBy || 'category']);
      summaryData.push(['Total Store Types:', warehouses?.length || 0]);
      summaryData.push(['Grand Total Quantity:', total || 0]);

      summaryData.push(['']);
      summaryData.push(['Store Type Summary:']);
      warehouses.forEach(storeType => {
        const storeTypeTotal = Object.values(hData[storeType] || {}).reduce((sum, cat) => sum + cat.total, 0);
        summaryData.push([
          storeType,
          storeTypeTotal,
          total > 0 ? ((storeTypeTotal / total) * 100).toFixed(1) + '%' : '0%'
        ]);
      });

      // Create summary worksheet
      const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);

      // Set summary column widths
      summaryWorksheet['!cols'] = [
        { wch: 25 }, // Labels
        { wch: 20 }, // Values
        { wch: 15 }  // Percentages
      ];

      // Add summary to workbook
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');

      // Generate filename
      const dateStr = format(new Date(), 'yyyy-MM-dd');
      const filename = `Store_Type_Report_${dateStr}.xlsx`;

      // Save file
      XLSX.writeFile(workbook, filename);

      return true;
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
      return false;
    }
  };

  const renderHierarchicalTable = () => {
    if (!hierarchicalData?.hierarchicalData || !hierarchicalData?.warehouses) {
      return (
        <div className="flex justify-center items-center h-32">
          <div className="text-gray-500">No hierarchical data available</div>
        </div>
      );
    }

    const { hierarchicalData: hData, warehouses } = hierarchicalData;

    // Determine which columns to show based on groupBy
    const showSKU = groupBy === "sku";
    const showProduct = groupBy === "sku" || groupBy === "product";
    const showCategory = true; // Always show category

    return (
      <div className="overflow-x-auto">
        <Table className="w-full">
          <TableHeader>
            <TableRow>
              <TableHead>Store Type</TableHead>
              {showCategory && <TableHead>Category</TableHead>}
              {showProduct && <TableHead>Product</TableHead>}
              {showSKU && <TableHead>SKU</TableHead>}
              <TableHead className="text-right">Quantity</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {warehouses.map(storeType => {
              const isStoreTypeExpanded = expandedStoreTypes[storeType];
              const storeTypeTotal = Object.values(hData[storeType] || {}).reduce((sum, cat) => sum + cat.total, 0);

              return (
                <React.Fragment key={storeType}>
                  {/* Store Type Header */}
                  <TableRow
                    className="bg-blue-100 font-bold cursor-pointer hover:bg-blue-200"
                    onClick={() => toggleStoreType(storeType)}
                  >
                    <TableCell className="flex items-center">
                      {isStoreTypeExpanded ? <ChevronDown className="h-4 w-4 mr-2" /> : <ChevronRight className="h-4 w-4 mr-2" />}
                      {storeType}
                    </TableCell>
                    {showCategory && <TableCell></TableCell>}
                    {showProduct && <TableCell></TableCell>}
                    {showSKU && <TableCell></TableCell>}
                    <TableCell className="text-right font-bold">{storeTypeTotal}</TableCell>
                  </TableRow>

                  {/* Expanded content - show all data based on groupBy */}
                  {isStoreTypeExpanded && Object.entries(hData[storeType] || {}).map(([category, categoryData]) => {

                    if (groupBy === "category") {
                      // Show only category level
                      return (
                        <TableRow key={`${storeType}-${category}`} className="hover:bg-gray-50">
                          <TableCell className="pl-8"></TableCell>
                          <TableCell>{category}</TableCell>
                          <TableCell className="text-right">{categoryData.total}</TableCell>
                        </TableRow>
                      );
                    } else if (groupBy === "product") {
                      // Show category -> product level
                      return Object.entries(categoryData.products || {}).map(([product, productData]) => (
                        <TableRow key={`${storeType}-${category}-${product}`} className="hover:bg-gray-50">
                          <TableCell className="pl-8"></TableCell>
                          <TableCell>{category}</TableCell>
                          <TableCell>{product}</TableCell>
                          <TableCell className="text-right">{productData.total}</TableCell>
                        </TableRow>
                      ));
                    } else if (groupBy === "sku") {
                      // Show category -> product -> sku level
                      return Object.entries(categoryData.products || {}).map(([product, productData]) =>
                        Object.entries(productData.skus || {}).map(([sku, skuData]) => (
                          <TableRow key={`${storeType}-${category}-${product}-${sku}`} className="hover:bg-gray-50">
                            <TableCell className="pl-8"></TableCell>
                            <TableCell>{category}</TableCell>
                            <TableCell>{product}</TableCell>
                            <TableCell>{sku}</TableCell>
                            <TableCell className="text-right">{skuData.quantity}</TableCell>
                          </TableRow>
                        ))
                      ).flat();
                    }

                    return null;
                  }).flat().filter(Boolean)}
                </React.Fragment>
              );
            })}

            {/* Grand Total Row */}
            <TableRow className="bg-yellow-100 font-bold border-t-2">
              <TableCell className="font-bold">TOTAL</TableCell>
              {showCategory && <TableCell></TableCell>}
              {showProduct && <TableCell></TableCell>}
              {showSKU && <TableCell></TableCell>}
              <TableCell className="text-right font-bold">{hierarchicalData.total}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-32">
          <div className="text-gray-500">Loading...</div>
        </div>
      );
    }

    if (!data?.tableData || data.tableData.length === 0) {
      return (
        <div className="flex justify-center items-center h-32">
          <div className="text-gray-500">No data available</div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {pieData && (
          <div>
            <h3 className="text-lg font-medium mb-2">Store Type Distribution</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="h-[400px] flex items-center justify-center">
                <Pie 
                  data={pieData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          usePointStyle: true,
                          padding: 20
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const value = context.raw;
                            const percentage = ((value / data.total) * 100).toFixed(1);
                            return `${context.label}: ${value} (${percentage}%)`;
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
              <div>
                <Table className="w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Store Type</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Percentage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.storeTypeSummary?.slice(0, 10).map((row, index) => (
                      <TableRow key={`store-${index}`}>
                        <TableCell className="whitespace-nowrap">
                          {row?.storeType || 'Unknown'}
                        </TableCell>
                        <TableCell className="text-right">{row?.quantity || 0}</TableCell>
                        <TableCell className="text-right">
                          {data.total > 0 ? ((row?.quantity / data.total) * 100).toFixed(1) : 0}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {(data.storeTypeSummary?.length || 0) > 10 && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-sm text-gray-500">
                          And {(data.storeTypeSummary?.length || 0) - 10} other store types
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow className="bg-gray-50">
                      <TableCell className="font-bold">Total</TableCell>
                      <TableCell className="text-right font-bold">{data.total || 0}</TableCell>
                      <TableCell className="text-right font-bold">100%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}

        {/* Hierarchical table */}
        <div>
         
          {renderHierarchicalTable()}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>
            Sale state by Store Type
            <span className="ml-2 text-sm font-normal">
              ({filters?.startDate && filters?.endDate ? 
                `${format(filters.startDate, "dd/MM/yyyy")} to ${format(filters.endDate, "dd/MM/yyyy")}` : 
                'No date range selected'
              })
            </span>
          </CardTitle>
          <Button
            onClick={exportToExcel}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            disabled={!data?.tableData || data.tableData.length === 0}
          >
            <Download className="h-4 w-4" />
            Export Excel
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default ByStoreTypeTab; 
